import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Radio } from 'lucide-react';
import { Breadcrumbs, BreadcrumbItem, PageHeader, SplitView, Section } from '@vnetwork/ui/layout';
import {
  Button,
  Stepper,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
  Input,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Spinner,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import {
  useProbeSource,
  useProvisionStream,
  type StreamKind,
  type DrmProbeResult,
} from '@/api/aes';
import { trackFeatureAction } from '@/lib/analytics-events';
import { KIND_LABEL, fmtDuration } from '../shared';
import { ProbeResultPanel } from './ProbeResultPanel';

const STEPS = ['Source', 'Probe', 'Policy', 'Review'];
// One broadcast-address segment: lowercase letters/digits, - and _ (URL-safe).
const SEGMENT_RE = /^[a-z0-9][a-z0-9_-]*$/;

export function StreamCreate({ base }: { base: string }) {
  const navigate = useNavigate();
  const { show } = useToast();

  const [step, setStep] = useState(0);
  const [reached, setReached] = useState(0);
  const [app, setApp] = useState('');
  const [name, setName] = useState('');
  const [kind, setKind] = useState<StreamKind>('live');
  const [sourceUrl, setSourceUrl] = useState('');
  const [periodSecs, setPeriodSecs] = useState(10);
  const [dvrSeconds, setDvrSeconds] = useState(3600);
  const [signedTtl, setSignedTtl] = useState(30);
  const [probe, setProbe] = useState<DrmProbeResult | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  // Set when sig-control answers 409 — the `<app>/<name>` address is taken.
  const [addressTaken, setAddressTaken] = useState(false);

  const appOk = SEGMENT_RE.test(app.trim());
  const nameOk = SEGMENT_RE.test(name.trim());
  const urlOk = /^https?:\/\/.+\.m3u8(\?.*)?$/i.test(sourceUrl.trim());
  const canProbe = appOk && nameOk && urlOk;
  const probeOk = !!probe && probe.is_clear;
  const address = `${app.trim()}/${name.trim()}`;

  const probeMut = useProbeSource();
  const provision = useProvisionStream();

  const goto = (i: number) => {
    setStep(i);
    setReached((r) => Math.max(r, i));
  };

  const runProbe = () => {
    if (!canProbe) {
      setShowErrors(true);
      return;
    }
    setShowErrors(false);
    probeMut.mutate(sourceUrl.trim(), {
      onSuccess: (res) => {
        setProbe(res);
        goto(1);
      },
      onError: (e) => show({ tone: 'danger', title: (e as Error)?.message || 'Probe failed' }),
    });
  };

  const submit = () => {
    setAddressTaken(false);
    provision.mutate(
      {
        app: app.trim(),
        name: name.trim(),
        source_url: sourceUrl.trim(),
        dvr_seconds: dvrSeconds,
        ...(kind === 'live' ? { period_secs: periodSecs } : {}),
      },
      {
        onSuccess: (res) => {
          show({ tone: 'success', title: `Stream "${res.stream.id}" provisioned` });
          trackFeatureAction('aes', 'stream', 'create');
          navigate(`${base}/streams/${encodeURIComponent(res.stream.id)}`);
        },
        onError: (e) => {
          // 409 = the address is already in use → inline error on step 0, no navigation.
          const status = (e as { response?: { status?: number } })?.response?.status;
          if (status === 409) {
            setAddressTaken(true);
            setShowErrors(true);
            setStep(0);
            show({ tone: 'danger', title: `Address "${address}" is already in use` });
            return;
          }
          show({ tone: 'danger', title: (e as Error)?.message || 'Provision failed' });
        },
      },
    );
  };

  const summary = (
    <Card flat>
      <CardHeader>
        <CardTitle>Summary</CardTitle>
        <CardDescription>Review before provisioning.</CardDescription>
      </CardHeader>
      <CardBody>
        <dl style={{ display: 'grid', gap: 'var(--vn-space-2)', margin: 0 }}>
          <Row label="Address" value={appOk || nameOk ? address : '—'} />
          <Row label="Kind" value={KIND_LABEL[kind]} />
          <Row label="Renditions" value={probe ? `${probe.variants.length}` : '—'} />
          {kind === 'live' && <Row label="Period" value={fmtDuration(periodSecs)} />}
          <Row label="DVR window" value={fmtDuration(dvrSeconds)} />
          <Row label="Signed-URL TTL" value={fmtDuration(signedTtl)} />
        </dl>
      </CardBody>
    </Card>
  );

  return (
    <>
      <PageHeader
        icon={<Radio size={20} />}
        breadcrumbs={
          <Breadcrumbs>
            <BreadcrumbItem asChild><Link to={base}>DRM Management</Link></BreadcrumbItem>
            <BreadcrumbItem asChild><Link to={`${base}/streams`}>Sources & Streams</Link></BreadcrumbItem>
            <BreadcrumbItem current>Add source</BreadcrumbItem>
          </Breadcrumbs>
        }
        title="Add source"
        description="Probe a CLEAR origin, then provision a protected LIVE or VOD stream."
      />

      <div style={{ marginBottom: 'var(--vn-space-5)' }}>
        <Stepper steps={STEPS} current={step} reached={reached} onStepClick={goto} />
      </div>

      <SplitView aside={summary} asideWidth="320px">
        <div style={{ display: 'grid', gap: 'var(--vn-space-4)', maxWidth: 720 }}>
          {step === 0 && (
            <Card flat>
              <CardHeader>
                <CardTitle>Source</CardTitle>
                <CardDescription>Choose a broadcast address and point at the CLEAR origin playlist.</CardDescription>
              </CardHeader>
              <CardBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
                <div style={{ display: 'grid', gap: 'var(--vn-space-4)', gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', maxWidth: 480 }}>
                  <Field
                    label="App"
                    required
                    description="Broadcast app, like an RTMP app."
                    error={showErrors && (!appOk || addressTaken) ? (addressTaken ? 'Address already in use.' : 'Lowercase letters, digits, - and _.') : undefined}
                  >
                    {(p) => (
                      <Input
                        {...p}
                        value={app}
                        onChange={(e) => { setApp(e.target.value.toLowerCase()); setAddressTaken(false); }}
                        placeholder="vtv"
                        invalid={showErrors && (!appOk || addressTaken)}
                      />
                    )}
                  </Field>
                  <Field
                    label="Name"
                    required
                    description="Stream name / key."
                    error={showErrors && (!nameOk || addressTaken) ? (addressTaken ? 'Address already in use.' : 'Lowercase letters, digits, - and _.') : undefined}
                  >
                    {(p) => (
                      <Input
                        {...p}
                        value={name}
                        onChange={(e) => { setName(e.target.value.toLowerCase()); setAddressTaken(false); }}
                        placeholder="vtv6plus"
                        invalid={showErrors && (!nameOk || addressTaken)}
                      />
                    )}
                  </Field>
                </div>
                {(appOk || nameOk) && (
                  <p style={{ margin: 0, fontSize: 'var(--vn-font-size-sm)', color: addressTaken ? 'var(--vn-danger-text)' : 'var(--vn-fg-muted)' }}>
                    Address: <code style={{ fontFamily: 'var(--vn-font-mono)' }}>{address}</code>
                  </p>
                )}
                <Field label="Kind">
                  {() => (
                    <Select value={kind} onValueChange={(v) => setKind(v as StreamKind)}>
                      <SelectTrigger style={{ maxWidth: 200 }}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="live">LIVE</SelectItem>
                        <SelectItem value="vod">VOD</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </Field>
                <Field
                  label="Source URL"
                  required
                  description="The partner's un-encrypted origin master playlist (.m3u8)."
                  error={showErrors && !urlOk ? 'Enter an http(s) .m3u8 URL.' : undefined}
                >
                  {(p) => (
                    <Input
                      {...p}
                      value={sourceUrl}
                      onChange={(e) => setSourceUrl(e.target.value)}
                      placeholder="https://origin.partner.tv/hls/vtv6plus/master.m3u8"
                      invalid={showErrors && !urlOk}
                    />
                  )}
                </Field>
              </CardBody>
            </Card>
          )}

          {step === 1 && (
            <Card flat>
              <CardHeader>
                <CardTitle>Probe result</CardTitle>
                <CardDescription>Detected ABR ladder and clear-check from the origin.</CardDescription>
              </CardHeader>
              <CardBody>
                {probeMut.isPending ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', color: 'var(--vn-fg-muted)' }}>
                    <Spinner size="sm" /> Probing source…
                  </div>
                ) : probe ? (
                  <ProbeResultPanel probe={probe} />
                ) : (
                  <Alert tone="info" title="No probe yet">Go back and probe the source first.</Alert>
                )}
              </CardBody>
            </Card>
          )}

          {step === 2 && (
            <Card flat>
              <CardHeader>
                <CardTitle>Key policy</CardTitle>
                <CardDescription>Rotation, DVR window and signed-URL lifetime.</CardDescription>
              </CardHeader>
              <CardBody>
                <div style={{ display: 'grid', gap: 'var(--vn-space-4)', gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', maxWidth: 480 }}>
                  {kind === 'live' && (
                    <Field label="Rotation period (seconds)">
                      {(p) => <Input {...p} type="number" value={String(periodSecs)} onChange={(e) => setPeriodSecs(Number(e.target.value) || 10)} />}
                    </Field>
                  )}
                  <Field label="DVR window (seconds)">
                    {(p) => <Input {...p} type="number" value={String(dvrSeconds)} onChange={(e) => setDvrSeconds(Number(e.target.value) || 0)} />}
                  </Field>
                  <Field label="Signed-URL TTL (seconds)">
                    {(p) => <Input {...p} type="number" value={String(signedTtl)} onChange={(e) => setSignedTtl(Number(e.target.value) || 30)} />}
                  </Field>
                </div>
              </CardBody>
            </Card>
          )}

          {step === 3 && (
            <Card flat>
              <CardHeader>
                <CardTitle>Ready to provision</CardTitle>
                <CardDescription>sig-control registers the stream, assigns a shard and starts the packager.</CardDescription>
              </CardHeader>
              <CardBody>
                <Section>
                  <p style={{ margin: 0, color: 'var(--vn-fg-muted)' }}>
                    Confirm the summary on the right. The stream begins provisioning immediately and goes live once the packager attaches to the source.
                  </p>
                </Section>
              </CardBody>
            </Card>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--vn-space-2)' }}>
            <Button variant="ghost" onClick={() => (step === 0 ? navigate(`${base}/streams`) : goto(step - 1))}>
              {step === 0 ? 'Cancel' : 'Back'}
            </Button>
            {step === 0 && (
              <Button onClick={runProbe} disabled={probeMut.isPending}>
                {probeMut.isPending ? 'Probing…' : 'Probe source'}
              </Button>
            )}
            {step === 1 && (
              <Button onClick={() => goto(2)} disabled={!probeOk}>
                Continue
              </Button>
            )}
            {step === 2 && <Button onClick={() => goto(3)}>Continue</Button>}
            {step === 3 && (
              <Button onClick={submit} disabled={provision.isPending}>
                {provision.isPending ? 'Provisioning…' : 'Provision stream'}
              </Button>
            )}
          </div>
        </div>
      </SplitView>
    </>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--vn-space-3)', fontSize: 'var(--vn-font-size-sm)' }}>
      <dt style={{ color: 'var(--vn-fg-muted)' }}>{label}</dt>
      <dd style={{ margin: 0, textAlign: 'right', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</dd>
    </div>
  );
}

export default StreamCreate;
